/**
 * File Utility Functions
 * Handles file operations like base64 conversion
 */

/**
 * Allowed image MIME types for base64 conversion
 */
const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/svg+xml",
];

/**
 * Result of a file to base64 conversion
 */
export interface FileToBase64Result {
  /** The base64-encoded data URL string */
  readonly base64: string;
  /** The original file's MIME type */
  readonly mimeType: string;
  /** The original filename */
  readonly fileName: string;
  /** The file size in bytes */
  readonly fileSize: number;
}

/**
 * Error thrown when file validation fails
 */
export class FileValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "FileValidationError";
  }
}

/**
 * Validates that a file is an allowed image type
 * @param file - The file to validate
 * @returns true if the file is a valid image type
 * @throws FileValidationError if the file type is not allowed
 */
export const validateImageFile = (file: File): boolean => {
  if (!file) {
    throw new FileValidationError("No file provided");
  }

  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    throw new FileValidationError(
      `Invalid file type: ${file.type}. Allowed types: ${ALLOWED_IMAGE_TYPES.join(", ")}`
    );
  }

  return true;
};

/**
 * Converts a File object to a base64-encoded data URL string
 * @param file - The file to convert
 * @param validateImage - Whether to validate that the file is an image (default: true)
 * @returns Promise resolving to the base64 result object
 * @throws FileValidationError if validation fails
 * @throws Error if file reading fails
 * 
 * @example
 * ```typescript
 * const result = await fileToBase64(file);
 * console.log(result.base64); // "data:image/png;base64,iVBORw0KGgo..."
 * console.log(result.mimeType); // "image/png"
 * ```
 */
export const fileToBase64 = (
  file: File,
  validateImage: boolean = true
): Promise<FileToBase64Result> => {
  return new Promise((resolve, reject) => {
    // Validate file if required
    if (validateImage) {
      try {
        validateImageFile(file);
      } catch (error) {
        reject(error);
        return;
      }
    }

    const reader = new FileReader();

    reader.onload = () => {
      const base64 = reader.result as string;
      resolve({
        base64,
        mimeType: file.type,
        fileName: file.name,
        fileSize: file.size,
      });
    };

    reader.onerror = () => {
      reject(new Error(`Failed to read file: ${file.name}`));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Extracts the raw base64 data from a data URL string (removes the "data:...;base64," prefix)
 * @param dataUrl - The data URL string
 * @returns The raw base64 data without the prefix
 * 
 * @example
 * ```typescript
 * const raw = extractBase64Data("data:image/png;base64,iVBORw0KGgo...");
 * console.log(raw); // "iVBORw0KGgo..."
 * ```
 */
export const extractBase64Data = (dataUrl: string): string => {
  const commaIndex = dataUrl.indexOf(",");
  if (commaIndex === -1) {
    return dataUrl;
  }
  return dataUrl.substring(commaIndex + 1);
};

/**
 * Checks if a string is a valid base64 data URL
 * @param str - The string to check
 * @returns true if the string is a valid data URL
 */
export const isBase64DataUrl = (str: string): boolean => {
  return str.startsWith("data:") && str.includes(";base64,");
};

